import { useState } from "react";
import { Reveal } from "./Reveal";
import { Counter } from "./Counter";

const TABS = [
  { id: "investment", label: "Initial Investment", testid: "financials-tab-investment" },
  { id: "revenue", label: "Unit Economics", testid: "financials-tab-revenue" },
  { id: "requirements", label: "Qualifications", testid: "financials-tab-requirements" },
];

const INVESTMENT_ROWS = [
  ["Initial Franchise Fee", "$49,500"],
  ["Leasehold Improvements & Build-Out", "$385,000 – $985,000"],
  ["Equipment, Signage & Opening Inventory", "$185,000 – $295,000"],
  ["Working Capital & Additional Funds", "$94,855 – $149,320"],
];

export function Financials() {
  const [tab, setTab] = useState("investment");

  return (
    <section id="financials" className="py-20 md:py-28 bg-[#F3EFE6]" data-testid="financials-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#FF6B4A] mb-4">The Economics</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0D3B3E] max-w-2xl">
            Transparent numbers. Serious potential.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 inline-flex flex-wrap gap-2 bg-white rounded-full p-1.5 border border-[#E4DFD3]">
            {TABS.map((t) => (
              <button
                key={t.id}
                data-testid={t.testid}
                onClick={() => setTab(t.id)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
                  tab === t.id ? "bg-[#0D3B3E] text-white" : "text-[#5C6B6C] hover:text-[#0D3B3E]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-8">
          {tab === "investment" && (
            <Reveal>
              <div className="grid lg:grid-cols-2 gap-5">
                <div className="bg-white rounded-3xl border border-[#E4DFD3] p-8">
                  {INVESTMENT_ROWS.map(([label, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-4 py-4 border-b border-[#E4DFD3] last:border-0">
                      <span className="text-sm text-[#5C6B6C]">{label}</span>
                      <span className="font-display font-bold text-[#0D3B3E] text-right">{value}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-[#0D3B3E] rounded-3xl p-10 flex flex-col justify-center">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#F7C948] font-semibold mb-3">Estimated Total Initial Investment*</div>
                  <div className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white" data-testid="financials-total-investment">
                    $664,355 – $1,478,820
                  </div>
                  <p className="mt-5 text-white/70 text-sm leading-relaxed max-w-md">
                    Includes the $49,500 franchise fee, build-out, equipment, and opening working capital for a standard
                    Dogtopia daycare, boarding & spa location.
                  </p>
                </div>
              </div>
            </Reveal>
          )}

          {tab === "revenue" && (
            <Reveal>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                <div className="bg-[#0D3B3E] rounded-3xl p-8 sm:col-span-2">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#F7C948] font-semibold mb-3">Average Gross Sales*</div>
                  <div className="font-display text-4xl lg:text-5xl font-extrabold text-white">
                    <Counter to={927566} prefix="$" testid="financials-avg-sales-counter" />
                  </div>
                  <p className="mt-3 text-white/70 text-sm">Median gross sales: $915,213</p>
                </div>
                <div className="bg-white rounded-3xl p-8 border border-[#E4DFD3]">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B4A] font-semibold mb-3">Avg Net Operating Margin*</div>
                  <div className="font-display text-4xl lg:text-5xl font-extrabold text-[#0D3B3E]">
                    <Counter to={18.5} decimals={1} suffix="%" testid="financials-margin-counter" />
                  </div>
                </div>
                <div className="bg-white rounded-3xl p-8 border border-[#E4DFD3]">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B4A] font-semibold mb-3">Ongoing Fees</div>
                  <div className="space-y-2 mt-2">
                    <div className="flex justify-between text-sm"><span className="text-[#5C6B6C]">Royalty</span><span className="font-display font-bold text-[#0D3B3E]">7%</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#5C6B6C]">Brand Fund</span><span className="font-display font-bold text-[#0D3B3E]">2%</span></div>
                    <div className="flex justify-between text-sm"><span className="text-[#5C6B6C]">Local Marketing</span><span className="font-display font-bold text-[#0D3B3E]">2%</span></div>
                  </div>
                </div>
              </div>
            </Reveal>
          )}

          {tab === "requirements" && (
            <Reveal>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="bg-white rounded-3xl p-10 border border-[#E4DFD3]">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B4A] font-semibold mb-3">Liquid Capital Required</div>
                  <div className="font-display text-4xl lg:text-5xl font-extrabold text-[#0D3B3E]">
                    <Counter to={300000} prefix="$" testid="financials-liquid-counter" />
                  </div>
                  <p className="mt-4 text-sm text-[#5C6B6C] leading-relaxed">
                    Readily available funds to invest. SBA and third-party financing options available for qualified candidates.
                  </p>
                </div>
                <div className="bg-white rounded-3xl p-10 border border-[#E4DFD3]">
                  <div className="text-xs uppercase tracking-[0.25em] text-[#FF6B4A] font-semibold mb-3">Net Worth Required</div>
                  <div className="font-display text-4xl lg:text-5xl font-extrabold text-[#0D3B3E]">
                    <Counter to={1000000} prefix="$" testid="financials-networth-counter" />
                  </div>
                  <p className="mt-4 text-sm text-[#5C6B6C] leading-relaxed">
                    Total net worth ensures you can capitalize the business through ramp-up to maturity.
                  </p>
                </div>
              </div>
            </Reveal>
          )}
        </div>

        <p className="mt-8 text-xs text-[#5C6B6C] max-w-3xl leading-relaxed">
          *Figures per the 2026 Franchise Disclosure Document (FDD), Item 7 and Item 19. A franchise is offered only through
          delivery of a current FDD. This page is a demonstration recreation.
        </p>
      </div>
    </section>
  );
}
